"use client";

import Image from "next/image";
import { L as Link } from "@/components/design";
import { useEffect, useRef, useState } from "react";
import { bySlug, rub } from "@/lib/catalog";
import { BUNDLE_OFF, cart, useCart } from "./cart";

const FREE_FROM = 5000;

export default function CartDrawer() {
  const lines = useCart();
  const dialog = useRef<HTMLDialogElement>(null);
  const [ordered, setOrdered] = useState<number | null>(null);

  useEffect(() => cart.onOpen(() => dialog.current?.showModal()), []);

  const rows = lines
    .map((l) => {
      const p = bySlug(l.slug);
      const v = p?.variants.find((x) => x.name === l.variant);
      const price = l.bundle ? Math.round((v?.price ?? 0) * (1 - BUNDLE_OFF)) : v?.price ?? 0;
      return p && v ? { l, p, v, price } : null;
    })
    .filter((x): x is NonNullable<typeof x> => !!x);
  const total = rows.reduce((n, r) => n + r.price * r.l.qty, 0);
  const left = Math.max(0, FREE_FROM - total);

  return (
    <dialog
      className="drawer"
      ref={dialog}
      aria-labelledby="cart-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) dialog.current?.close();
      }}
      onClose={() => setOrdered(null)}
    >
      <div className="drawer__in">
        <div className="drawer__top">
          <div>
            <p className="eyebrow label">Собрано для выхода в поле</p>
            <h2 id="cart-title">Корзина</h2>
          </div>
          <button type="button" className="x" aria-label="Закрыть корзину" onClick={() => dialog.current?.close()}>
            ×
          </button>
        </div>

        {ordered ? (
          <div className="drawer__done" role="status">
            <h3>Заказ № {ordered} принят</h3>
            <p>Магазин демонстрационный: корзина, скидки и подсчёт настоящие, а доставлять нечего. Адрес и телефон не спрашиваем — голубь-курьер всё равно их не запомнит.</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="drawer__empty">
            <p>Пока только любопытство. С него всё и начинается.</p>
            <Link className="btn btn-p label" href="/catalog" onClick={() => dialog.current?.close()}>
              Перейти в каталог
            </Link>
          </div>
        ) : (
          <>
            <div className="ship" aria-live="polite">
              <p className="label">{left ? `До бесплатной доставки: ${rub(left)}` : "Доставка бесплатная. Голубь-курьер доволен"}</p>
              <i>
                <span style={{ width: `${Math.min(100, (total / FREE_FROM) * 100)}%` }} />
              </i>
            </div>
            <ul className="lines">
              {rows.map(({ l, p, v, price }) => (
                <li key={`${l.slug}-${l.variant}-${l.bundle ? "b" : ""}`}>
                  <Image src={p.images[0].src} alt="" width={88} height={88} />
                  <div>
                    <Link href={`/catalog/${p.slug}`} onClick={() => dialog.current?.close()}>
                      {p.name}
                    </Link>
                    {p.variants.length > 1 && <small>{v.name}</small>}
                    {l.bundle && <small className="in-bundle">В экспедиции −{BUNDLE_OFF * 100}%</small>}
                    <div className="qty qty--sm" role="group" aria-label={`Количество: ${p.name}`}>
                      <button type="button" aria-label="Меньше" onClick={() => cart.set(l, l.qty - 1)}>
                        −
                      </button>
                      <output>{l.qty}</output>
                      <button type="button" aria-label="Больше" onClick={() => cart.set(l, l.qty + 1)}>
                        +
                      </button>
                    </div>
                  </div>
                  <b>{rub(price * l.qty)}</b>
                </li>
              ))}
            </ul>
            <div className="drawer__sum">
              <span>Итого</span>
              <b>{rub(total)}</b>
            </div>
            <button
              type="button"
              className="btn btn-p label"
              onClick={() => {
                setOrdered(41700 + rows.length * 3 + total % 97);
                cart.clear();
              }}
            >
              Оформить заказ
            </button>
            <p className="drawer__note">Корзина хранится только в этом браузере. Демо: оплаты нет, данные не собираются.</p>
          </>
        )}
      </div>
    </dialog>
  );
}
